import axios from "axios";

export const PLATFORM_NAME = "LimoLinkX";

/** Each app keeps its own session slot, so chauffeur, client, office and admin can be signed in
 *  side by side in the same browser exactly like separate apps. */
export function appScope(pathname = window.location.pathname) {
  if (pathname.startsWith("/admin")) return "admin";
  if (pathname.startsWith("/portal/driver") || pathname.startsWith("/drivers")) return "driver";
  if (pathname.startsWith("/portal/client") || pathname.startsWith("/clients")
      || pathname.startsWith("/portal/invoice")) return "client";
  // Passenger and chauffeur links carry their own token in the URL, so these pages must never
  // borrow a signed-in session from another app on the same phone.
  if (PUBLIC_PATHS.test(pathname)) return "public";
  return "office";
}

const PUBLIC_PATHS = /^\/(ride|book|r|track|receipt|pay|statement|corporate|winback|booking|driver\/trip)\b/;

const tokenKey = () => `fm_token_${appScope()}`;

// Retire the legacy shared slot for good: it held whichever app signed in last, which is how a
// passenger could find themselves inside the chauffeur app.
localStorage.removeItem("fm_token");

export const getToken = () => localStorage.getItem(tokenKey());
export const setToken = (token) => {
  localStorage.setItem(tokenKey(), token);
  localStorage.removeItem("fm_token");  // retire the old shared slot
};
export const clearToken = () => {
  localStorage.removeItem(tokenKey());
  localStorage.removeItem("fm_token");
};

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// A container that has been idle takes a moment to answer its first request. Rather than showing
// "could not reach the server" and making people reload, retry a read twice before giving up.
const RETRY_LIMIT = 2;
api.interceptors.response.use(undefined, async (error) => {
  const config = error?.config;
  const retriable = config && !error.response && (config.method || "get").toLowerCase() === "get";
  if (!retriable) return Promise.reject(error);
  config.__retries = (config.__retries || 0) + 1;
  if (config.__retries > RETRY_LIMIT) return Promise.reject(error);
  await new Promise((r) => setTimeout(r, 1200 * config.__retries));
  return api(config);
});

export function apiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function errMsg(e) {
  // No response at all means the request never landed — a restarting server, a dropped
  // connection or offline. Saying "something went wrong" for that sends people hunting for a
  // password problem they do not have.
  if (e && !e.response) {
    return e.code === "ECONNABORTED"
      ? "That took too long to respond. Check your connection and try again."
      : "Could not reach the server. Check your connection and try again in a moment.";
  }
  const status = e?.response?.status;
  if (status >= 500) return "The server hit a problem with that. Please try again in a moment.";
  return apiError(e?.response?.data?.detail) || e?.message || "Unexpected error";
}

export const money = (n) =>
  `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default api;

// Stripe Checkout refuses to render inside an iframe (the preview shell frames the app), so a
// same-frame redirect just goes blank. Break out to the top window, and fall back to a manual link.
export function openCheckout(url, onBlocked) {
  if (!url) return false;
  const framed = window.self !== window.top;
  if (!framed) {
    window.location.href = url;
    return true;
  }
  // Framed (preview shell, or an embedded booking widget on the operator's own site): hosted
  // checkouts refuse to render in an iframe and a cross-origin top navigation can be blocked
  // silently, so hand over a real link straight away and try to break out at the same time.
  if (typeof onBlocked === "function") onBlocked(url);
  try {
    window.top.location.href = url;
    return true;
  } catch {
    const tab = window.open(url, "_blank", "noopener");
    return Boolean(tab);
  }
}
